import clsx from 'clsx';
import { forwardRef, type PointerEventHandler } from 'react';

import * as styles from './launcher-sidebar-layout.css';

/** How far below the smallest width a drag has to go to collapse the sidebar: half of it. */
export const sidebarCollapseWidth = (minWidth: number) => minWidth / 2;

/** Stand a sidebar's divider at a boundary on the way between open and collapsed. */
export function placeSidebarDivider(
  divider: HTMLElement | null,
  boundary: number
) {
  if (!divider) return;
  divider.style.left = `${boundary}px`;
  divider.dataset.moving = 'true';
}

/** Hand the divider back to its resting place, open or collapsed. */
export function releaseSidebarDivider(divider: HTMLElement | null) {
  if (!divider) return;
  divider.style.removeProperty('left');
  delete divider.dataset.moving;
}

/**
 * The boundary of a collapsible sidebar: a hairline that is dragged to set the width. Dragged far
 * below the smallest width it collapses the sidebar, a double click restores the standard width.
 * Collapsed, nothing of the sidebar remains; the view's edge can be pulled to bring it back.
 * It stands at `--launcher-pane-width` of the layout around it.
 */
export const LauncherSidebarDivider = forwardRef<
  HTMLDivElement,
  {
    open: boolean;
    /** The sidebar stays beside its view, so its boundary can be dragged. */
    resizable: boolean;
    className?: string;
    resizeLabel: string;
    onResizeStart: PointerEventHandler<HTMLDivElement>;
    onResetWidth: () => void;
    testId?: string;
  }
>(function LauncherSidebarDivider(
  {
    open,
    resizable,
    className,
    resizeLabel,
    onResizeStart,
    onResetWidth,
    testId = 'launcher-sidebar',
  },
  ref
) {
  return (
    <div
      ref={ref}
      className={clsx(styles.divider, className)}
      data-open={open}
      data-resizable={resizable}
      data-testid={`${testId}-divider`}
      role={resizable ? 'separator' : undefined}
      aria-orientation={resizable ? 'vertical' : undefined}
      aria-label={resizable ? resizeLabel : undefined}
      onPointerDown={resizable ? onResizeStart : undefined}
      onDoubleClick={resizable && open ? onResetWidth : undefined}
    />
  );
});
